import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields } from '../../../../config/builder-schemas'
import { AGGREGATION_TYPES, AVAILABLE_ICONS, AVAILABLE_COLORS } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function KPIConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  // Get fields from dynamic data sources or fallback to static
  const aggregatableFields = useMemo(() => {
    if (!config.dataSource) return []

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      return fieldsFromCustomQuery(config.customQuery)
        .filter((f) => f.aggregatable)
        .map((f) => ({
          name: f.name,
          label: f.label,
          aggregatable: true as const,
          description: undefined as string | undefined,
        }))
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return dynamicSource.fields.filter(f => f.aggregatable)
    }

    return getAggregatableFields(config.dataSource)
  }, [dataSources, config.dataSource, cqSig])

  const isValid = Boolean(
    config.metadata?.title &&
    config.dataField &&
    config.aggregation
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-6">Configurar KPI</h2>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Nombre personalizado */}
        <div>
          <label className="block font-medium mb-2">
            Nombre del Indicador <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Volumen Total de Madera"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
          <p className="text-sm text-gray-500 mt-1">
            Este nombre aparecerá en el dashboard público
          </p>
        </div>

        {/* Campo a calcular */}
        <div>
          <label className="block font-medium mb-2">
            Campo Numérico <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.dataField || ''}
            onChange={(e) => onChange({ ...config, dataField: e.target.value })}
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          {config.dataField && (
            <p className="text-sm text-gray-500 mt-1">
              {aggregatableFields.find(f => f.name === config.dataField)?.description}
            </p>
          )}
        </div>

        {/* Tipo de agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-4 text-center transition-all
                  ${
                    config.aggregation === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() => onChange({ ...config, aggregation: agg.value as any })}
              >
                <div className="text-2xl mb-1">{agg.icon}</div>
                <div className="font-medium text-sm">{agg.value}</div>
                <div className="text-xs text-gray-500 mt-1">{agg.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Unidad de medida */}
        <div>
          <label className="block font-medium mb-2">Unidad de Medida</label>
          <input
            type="text"
            placeholder="Ej: m³, ha, árboles, %"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.styling?.unit || ''}
            onChange={(e) =>
              onChange({
                ...config,
                styling: { ...config.styling, unit: e.target.value },
              })
            }
          />
        </div>

        {/* Icono */}
        <div>
          <label className="block font-medium mb-2">Icono (Emoji)</label>
          <div className="flex gap-2 flex-wrap">
            {AVAILABLE_ICONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={`
                  text-3xl p-3 border-2 rounded hover:bg-gray-50 transition-colors
                  ${
                    config.styling?.icon === emoji
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    styling: { ...config.styling, icon: emoji },
                  })
                }
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="block font-medium mb-2">Color Temático</label>
          <div className="flex gap-3 flex-wrap">
            {AVAILABLE_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                className={`
                  ${color.bg} text-white px-4 py-2 rounded font-medium transition-all
                  ${
                    config.styling?.color === color.value
                      ? 'ring-4 ring-offset-2 ring-green-500'
                      : 'opacity-70 hover:opacity-100'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    styling: { ...config.styling, color: color.value as any },
                  })
                }
              >
                {color.label}
              </button>
            ))}
          </div>
        </div>

        {/* Botón continuar */}
        <button
          onClick={onNext}
          disabled={!isValid}
          className="w-full bg-green-600 text-white py-3 rounded font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed mt-6"
        >
          Continuar a Vista Previa →
        </button>
      </div>
    </div>
  )
}
